import { PlaywrightAction } from './PlaywrightAction.js';
/**
 * Logging levels for Probo
 */
export var ProboLogLevel;
(function (ProboLogLevel) {
    ProboLogLevel["DEBUG"] = "DEBUG";
    ProboLogLevel["INFO"] = "INFO";
    ProboLogLevel["LOG"] = "LOG";
    ProboLogLevel["WARN"] = "WARN";
    ProboLogLevel["ERROR"] = "ERROR";
})(ProboLogLevel || (ProboLogLevel = {}));
const logLevelOrder = {
    [ProboLogLevel.DEBUG]: 0,
    [ProboLogLevel.INFO]: 1,
    [ProboLogLevel.LOG]: 2,
    [ProboLogLevel.WARN]: 3,
    [ProboLogLevel.ERROR]: 4,
};
export class ProboLogger {
    constructor(prefix, level = ProboLogLevel.INFO) {
        this.prefix = prefix;
        this.level = level;
    }
    setLogLevel(level) {
        console.log(`[${this.prefix}] Setting log level to: ${level} (was: ${this.level})`);
        this.level = level;
    }
    shouldLog(level) {
        return logLevelOrder[level] >= logLevelOrder[this.level];
    }
    preamble(level) {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
        return `[${hours}:${minutes}:${seconds}.${milliseconds}] [${this.prefix}] [${level}]`;
    }
    debug(...args) { if (this.shouldLog(ProboLogLevel.DEBUG))
        console.debug(this.preamble(ProboLogLevel.DEBUG), ...args); }
    info(...args) { if (this.shouldLog(ProboLogLevel.INFO))
        console.info(this.preamble(ProboLogLevel.INFO), ...args); }
    log(...args) { if (this.shouldLog(ProboLogLevel.LOG))
        console.log(this.preamble(ProboLogLevel.LOG), ...args); }
    warn(...args) { if (this.shouldLog(ProboLogLevel.WARN))
        console.warn(this.preamble(ProboLogLevel.WARN), ...args); }
    error(...args) { if (this.shouldLog(ProboLogLevel.ERROR))
        console.error(this.preamble(ProboLogLevel.ERROR), ...args); }
}
const fetchLogger = new ProboLogger('fetch');
function formatFetchErrorDetails(error, depth = 0) {
    if (!error || depth > 2) {
        return String(error);
    }
    if (error instanceof Error) {
        const err = error;
        const details = {
            name: err.name,
            message: err.message
        };
        for (const key of ['code', 'errno', 'syscall', 'hostname', 'address', 'port', 'type']) {
            if (err[key] !== undefined) {
                details[key] = err[key];
            }
        }
        if (err.cause !== undefined) {
            details.cause = formatFetchErrorDetails(err.cause, depth + 1);
        }
        return details;
    }
    if (typeof error === 'object') {
        const err = error;
        const details = {};
        for (const key of ['name', 'message', 'code', 'errno', 'syscall', 'hostname', 'address', 'port', 'type']) {
            if (err[key] !== undefined) {
                details[key] = err[key];
            }
        }
        if (err.cause !== undefined) {
            details.cause = formatFetchErrorDetails(err.cause, depth + 1);
        }
        return Object.keys(details).length > 0 ? details : String(error);
    }
    return String(error);
}
function getFetchUrl(input) {
    if (typeof input === 'string')
        return input;
    if (input instanceof URL)
        return input.toString();
    if (typeof input === 'object' && input && 'url' in input) {
        const url = input.url;
        if (typeof url === 'string')
            return url;
    }
    return 'unknown-url';
}
function getFetchMethod(input, init) {
    if (init === null || init === void 0 ? void 0 : init.method)
        return init.method;
    if (typeof input === 'object' && input && 'method' in input) {
        const method = input.method;
        if (typeof method === 'string')
            return method;
    }
    return 'GET';
}
export async function fetchWithLogging(input, init, context) {
    try {
        return await fetch(input, init);
    }
    catch (error) {
        const url = getFetchUrl(input);
        const method = getFetchMethod(input, init);
        const meta = {
            source: context === null || context === void 0 ? void 0 : context.source,
            operation: context === null || context === void 0 ? void 0 : context.operation,
            method,
            url,
            details: formatFetchErrorDetails(error)
        };
        fetchLogger.error('Fetch failed', meta);
        throw error;
    }
}
// Element cleaner logging
// const elementLogger = new ProboLogger('element-cleaner');
/**
 * Cleans and returns a minimal element info structure.
 */
//TODO: is this needed?
/* export function cleanupElementInfo(elementInfo: ElementInfo): CleanElementInfo {
  elementLogger.debug(
    `Cleaning up element info for ${elementInfo.tag} at index ${elementInfo.index}`
  );
  const depth = elementInfo.depth ?? elementInfo.getDepth();
  const cleanEl = {
    index: elementInfo.index,
    tag: elementInfo.tag,
    type: elementInfo.type,
    text: elementInfo.text,
    html: elementInfo.html,
    xpath: elementInfo.xpath,
    css_selector: elementInfo.css_selector,
    iframe_selector: elementInfo.iframe_selector,
    bounding_box: elementInfo.bounding_box,
    depth
  };
  elementLogger.debug(`Cleaned element: ${JSON.stringify(cleanEl)}`);
  return cleanEl;
} */
/**
 * Cleans highlighted elements in an instruction payload.
 */
/* export function cleanupInstructionElements(instruction: any): any {
  if (!instruction?.result?.highlighted_elements) {
    elementLogger.debug('No highlighted elements to clean');
    return instruction;
  }
  elementLogger.debug(
    `Cleaning ${instruction.result.highlighted_elements.length} highlighted elements`
  );
  const cleaned = {
    ...instruction,
    result: {
      ...instruction.result,
      highlighted_elements: instruction.result.highlighted_elements.map(
        (el: ElementInfo) => cleanupElementInfo(el)
      )
    }
  };
  elementLogger.debug('Instruction cleaning completed');
  return cleaned;
} */
// Determine whether an interaction can return a value
export const hasReturnValue = (i) => {
    return [
        PlaywrightAction.EXTRACT_VALUE,
        PlaywrightAction.ASK_AI,
        PlaywrightAction.EXECUTE_SCRIPT
    ].includes(i.action);
};
export const getReturnValueParameterName = (i) => {
    switch (i.action) {
        case PlaywrightAction.EXTRACT_VALUE:
        case PlaywrightAction.EXECUTE_SCRIPT:
            return i.parameterName;
        case PlaywrightAction.ASK_AI:
            return i.parameterName.replace(/^assert_/, '');
        default:
            console.error(`Action ${i.action} has no return value`);
            return '';
    }
};
// Determine whether an interaction can be parameterized
export const isParameterizable = (i) => {
    const parameterizableActions = [
        PlaywrightAction.FILL_IN,
        PlaywrightAction.SELECT_DROPDOWN,
        PlaywrightAction.SET_SLIDER,
        PlaywrightAction.ASSERT_CONTAINS_VALUE,
        PlaywrightAction.ASSERT_EXACT_VALUE,
        PlaywrightAction.VISIT_URL,
        PlaywrightAction.ASSERT_URL,
        PlaywrightAction.UPLOAD_FILES,
        PlaywrightAction.WAIT_FOR,
        PlaywrightAction.GEN_TOTP,
        PlaywrightAction.WAIT_FOR_OTP
    ];
    return parameterizableActions.includes(i.action) || (i.action === PlaywrightAction.ASK_AI && i.argument);
};
// Determine whether an interaction is AI-related
export const isAI = (i) => {
    var _a, _b, _c, _d, _e, _f;
    return !['TYPE_KEYS', 'VISIT_URL', 'EXECUTE_SCRIPT'].includes(i.action) &&
        (i.action === PlaywrightAction.ASK_AI ||
            (((_b = (_a = i.serverResponse) === null || _a === void 0 ? void 0 : _a.result) === null || _b === void 0 ? void 0 : _b.prompt) && (((_d = (_c = i.serverResponse) === null || _c === void 0 ? void 0 : _c.result) === null || _d === void 0 ? void 0 : _d.error) === "" || !((_f = (_e = i.serverResponse) === null || _e === void 0 ? void 0 : _e.result) === null || _f === void 0 ? void 0 : _f.error))));
};
// Determine whether an interaction is fortified (doesn't need AI processing)
export const isFortifiedInteraction = (i) => {
    return [
        PlaywrightAction.VISIT_URL,
        PlaywrightAction.ASSERT_URL,
        PlaywrightAction.TYPE_KEYS,
        PlaywrightAction.EXECUTE_SCRIPT,
        PlaywrightAction.ASK_AI
    ].includes(i.action);
};
/**
 * Check if an interaction is a sequence marker (SEQUENCE_START or SEQUENCE_END)
 * @param action - The PlaywrightAction to check
 * @returns true if the action is a sequence marker
 */
export function isSequenceMarker(action) {
    return action === PlaywrightAction.SEQUENCE_START || action === PlaywrightAction.SEQUENCE_END;
}
export function singleQuoteString(str) {
    if (!str)
        return '';
    return `'${str.replace(/'/g, "\\'")}'`;
}
export function doubleQuoteString(str) {
    if (!str)
        return '';
    return `"${str.replace(/"/g, '\\"')}"`;
}
/**
 * Extract unique process.env variable names from a parameter table.
 * Looks for ${process.env.VAR_NAME} patterns in string values.
 */
export function extractEnvVarsFromParameterTable(parameterTable) {
    if (!Array.isArray(parameterTable) || parameterTable.length === 0) {
        return [];
    }
    const envVars = new Set();
    const pattern = /\$\{process\.env\.([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    parameterTable.forEach((row) => {
        Object.values(row).forEach((value) => {
            if (typeof value !== 'string')
                return;
            let match;
            while ((match = pattern.exec(value)) !== null) {
                envVars.add(match[1]);
            }
        });
    });
    return Array.from(envVars).sort();
}
/**
 * Converts a string to a filesystem-safe slug.
 * Used for filenames/package names (not URL parsing).
 */
export function slugify(text) {
    if (!text)
        return 'scenario';
    return text
        .toLowerCase()
        .trim()
        .replace(/[^a-zA-Z0-9-_]/g, '-') // Replace non-alphanumeric chars with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
        .substring(0, 100); // Limit length to 100 chars
}
// Normalize a parameter name to a legal JS identifier
export function normalizeParameterName(name) {
    // 0. Remove leading/trailing spaces
    // 1. Replace invalid characters with underscores
    // 2. collapse multiple underscores
    // 3. Remove trailing underscores
    // 4. If starts with a digit, prepend '_'
    // 5. Convert to lowercase
    name = name.trim();
    name = name.replace(/[^a-zA-Z0-9_$]/g, '_');
    name = name.replace(/_+/g, '_');
    name = name.replace(/_+$/, ''); // Remove trailing underscores
    if (/^[0-9]/.test(name)) {
        name = '_' + name;
    }
    name = name.toLowerCase();
    // Ensure we never return an empty string or just underscores
    if (!name || name === '_' || /^_+$/.test(name)) {
        name = 'param_' + Date.now();
    }
    return name;
}
export function matchRegex(str, regex) {
    //parse the regex string
    const match = regex.match(/^\/(.+)\/([gimsuy]*)$/);
    if (!match) // normal string
        return str.includes(regex);
    else { // regex string
        const pattern = match[1].replace(/\\/g, '\\');
        const flags = match[2];
        console.log(`Matching ${str} against ${pattern} with flags ${flags}`);
        return new RegExp(pattern, flags).test(str);
    }
}
/**
 * Truncates a URL by replacing query parameters (everything after ?) with three dots.
 * @param url The URL to truncate
 * @returns The truncated URL with three dots replacing query parameters
 */
export function truncateUrl(url) {
    if (!url)
        return '';
    const questionMarkIndex = url.indexOf('?');
    if (questionMarkIndex === -1) {
        // No query parameters found, return the original URL
        return url;
    }
    const baseUrl = url.substring(0, questionMarkIndex);
    return `${baseUrl}...`;
}
/**
 * Sets up browser console logging on a Playwright page.
 * This function removes all existing console listeners and optionally adds a new one.
 * @param page The Playwright page instance
 * @param enableConsoleLogs Whether to enable console logging
 * @param logger Optional logger instance to use for output (defaults to console.log)
 */
export const setupBrowserConsoleLogs = (page, enableConsoleLogs, logger = null) => {
    // Always remove all existing console listeners first
    page.removeAllListeners('console');
    // Always add a listener, but filter output based on enableConsoleLogs
    const listener = (msg) => {
        if (enableConsoleLogs) {
            const type = msg.type();
            const text = msg.text();
            if (logger) {
                logger.log(`[Browser-${type}]: ${text}`);
            }
            else {
                console.log(`[Browser-${type}]: ${text}`);
            }
        }
        // If disabled, do nothing (silently ignore the console message)
    };
    page.on('console', listener);
};
/**
 * Safely interpolates template literals in a string using the provided context.
 * Similar to JavaScript template literals, but executed safely at runtime.
 * Recursively interpolates until no more template literals remain.
 *
 * This function only interpolates the argument string itself, not the entire context.
 * When a template literal resolves to another string containing template literals,
 * it recursively interpolates that result.
 *
 * @param str The string containing template literal syntax (e.g., "Hello ${name}")
 * @param context The context object containing variables for interpolation
 * @param maxDepth Maximum recursion depth to prevent infinite loops (default: 10)
 * @returns The interpolated string, or the original string if no interpolation is needed
 */
export function interpolateTemplate(str, context, maxDepth = 10) {
    if (typeof str !== 'string' || !str.includes('${')) {
        return str;
    }
    if (maxDepth <= 0) {
        console.warn('⚠️ Maximum interpolation depth reached, returning partially interpolated string');
        return str;
    }
    try {
        // Escape backticks in the template to prevent template literal injection
        const escapedTemplate = str.replace(/\\/g, '\\\\').replace(/`/g, '\\`');
        // Create a safe template execution function
        // Assign all context properties to variables so they're accessible in the template
        // This works for both simple values and nested objects like process.env
        const contextKeys = Object.keys(context);
        const assignments = contextKeys.map(key => `const ${key} = ctx.${key};`).join('\n');
        const functionBody = `
      const ctx = arguments[0];
      ${assignments}
      return \`${escapedTemplate}\`;
    `;
        const compiled = new Function(functionBody);
        const rendered = compiled(context);
        // If the result still contains template literals, recursively interpolate
        if (rendered.includes('${') && rendered !== str) {
            return interpolateTemplate(rendered, context, maxDepth - 1);
        }
        return rendered;
    }
    catch (e) {
        const error = e instanceof Error ? e : new Error(String(e));
        console.error('⚠️ Template evaluation failed:', error);
        throw error;
    }
}
export class Mutex {
    constructor() {
        this._locked = false;
        this._waiters = [];
    }
    lock() {
        return new Promise((resolve) => {
            const unlock = () => {
                if (this._waiters.length > 0) {
                    const nextResolve = this._waiters.shift();
                    if (nextResolve) {
                        nextResolve(unlock);
                    }
                }
                else {
                    this._locked = false;
                }
            };
            if (this._locked) {
                this._waiters.push(resolve);
            }
            else {
                this._locked = true;
                resolve(unlock);
            }
        });
    }
    async withLock(fn) {
        const unlock = await this.lock();
        try {
            return await fn();
        }
        finally {
            unlock();
        }
    }
}
//# sourceMappingURL=utils.js.map